<template>
  <div ref="rootRef" :dir="isRtl !== false ? 'rtl' : 'ltr'" class="relative w-full custom-selector-container">
    <div class="flex w-full flex-col items-stretch sm:flex-row">
      <button
        type="button"
        :class="['relative flex min-h-[3.25rem] min-w-0 flex-1 items-center justify-start rounded-2xl border border-base-300/80 bg-base-100 px-4 py-3 shadow-sm transition-all duration-200 hover:border-base-content/15 hover:bg-base-200/40 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-base-content/20 dark:border-gray-700/90 dark:bg-gray-900/35 dark:hover:border-gray-600 dark:hover:bg-gray-800/50', isRtl === true ? 'text-right' : 'text-left']"
        @click="toggleDropdown"
        aria-haspopup="listbox"
        :aria-expanded="isOpen !== false"
      >
        <div v-if="selectedVoiceObj !== null" class="flex w-full min-w-0 items-center" style="padding-inline-end: 1.75rem;">
          <div class="relative min-w-0 flex-1 overflow-hidden" :class="isRtl === true ? 'text-right' : 'text-left'">
            <span class="block w-full truncate text-sm font-semibold tracking-tight text-base-content">{{ selectedVoiceDisplayName }}</span>
            <span v-if="buildMetaLine(selectedVoiceObj) !== ''" class="mt-1 block w-full truncate text-[11px] text-base-content/50">
              {{ buildMetaLine(selectedVoiceObj) }}
            </span>
          </div>
        </div>
        <div v-else class="flex w-full min-w-0 items-center" style="padding-inline-end: 1.75rem;">
          <span class="text-sm text-base-content/60">{{ placeholder }}</span>
        </div>
        <Icon icon="heroicons:chevron-down-20-solid" class="pointer-events-none absolute top-1/2 h-5 w-5 -translate-y-1/2 text-base-content/45 transition-transform duration-200" :class="[isRtl === true ? 'left-4' : 'right-4', isOpen === true ? 'rotate-180' : '']" />
      </button>
      <button
        type="button"
        :class="['mt-3 inline-flex min-h-[3.25rem] h-auto w-full flex-shrink-0 items-center justify-center rounded-2xl border border-base-300/80 bg-base-100 text-base-content shadow-sm transition-all duration-200 hover:border-base-content/15 hover:bg-base-200/50 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-base-content/20 dark:border-gray-700/90 dark:bg-gray-900/35 dark:hover:bg-gray-800/50 sm:mt-0 sm:w-[3.25rem]', isRtl === true ? 'sm:mr-3' : 'sm:ml-3']"
        @click="openFullScreen"
        :aria-label="t('voiceSelector.fullScreen', 'Open full screen voice browser')"
      >
        <Icon icon="mdi:arrow-expand-all" class="h-5 w-5 opacity-80" />
      </button>
    </div>

    <Teleport to="body">
      <transition
        enter-active-class="transition ease-out duration-100"
        enter-from-class="transform opacity-0 scale-95"
        enter-to-class="transform opacity-100 scale-100"
        leave-active-class="transition ease-in duration-75"
        leave-from-class="transform opacity-100 scale-100"
        leave-to-class="transform opacity-0 scale-95"
      >
        <div
          v-if="isOpen === true"
          ref="dropdownListRef"
          class="flex max-h-full flex-col overflow-hidden rounded-2xl border border-base-300/90 bg-base-100 shadow-xl ring-1 ring-black/5 dark:border-gray-700 dark:bg-gray-900 dark:ring-white/10"
          :style="dropdownPanelStyle"
        >
        <div class="shrink-0 border-b border-base-300/80 p-2 dark:border-gray-700/80">
          <div class="relative">
            <input
              ref="searchInputRef"
              v-model="searchQuery"
              type="text"
              class="input input-bordered h-9 w-full rounded-xl border-base-300/80 bg-base-100 pr-10 transition-shadow focus:border-base-content/20 focus:outline-none focus:ring-2 focus:ring-base-content/10 dark:border-gray-700 dark:bg-gray-950"
              :class="isRtl === true ? 'pl-10 pr-4 text-right' : 'pr-10'"
              :placeholder="t('voiceSelector.searchPlaceholder', 'Search voice, accent or language')"
            />
            <Icon
              icon="mdi:magnify"
              class="pointer-events-none absolute top-1/2 h-5 w-5 -translate-y-1/2 text-base-content/45"
              :class="isRtl === true ? 'left-3' : 'right-3'"
            />
          </div>
          <div class="mt-2 flex flex-row flex-nowrap space-x-2 overflow-x-auto pb-1 rtl:space-x-reverse hide-scrollbar">
            <button
              type="button"
              class="cursor-pointer whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-all duration-150"
              :class="selectedLanguageFilter === '' ? 'border-base-content/20 bg-base-content/10 text-base-content shadow-sm' : 'border-transparent bg-base-200/70 text-base-content/70 hover:bg-base-200 hover:text-base-content dark:bg-gray-800/80 dark:hover:bg-gray-800'"
              @click="selectedLanguageFilter = ''"
            >
              {{ t('voiceSelector.all', 'All') }}
            </button>
            <button
              v-for="lang in availableLanguages"
              :key="lang"
              type="button"
              class="cursor-pointer whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-all duration-150"
              :class="selectedLanguageFilter === lang ? 'border-base-content/20 bg-base-content/10 text-base-content shadow-sm' : 'border-transparent bg-base-200/70 text-base-content/70 hover:bg-base-200 hover:text-base-content dark:bg-gray-800/80 dark:hover:bg-gray-800'"
              @click="selectedLanguageFilter = lang"
            >
              {{ formatLanguageLabel(lang) }}
            </button>
          </div>
          <div class="mt-2 flex items-center justify-between">
            <span class="text-xs text-base-content/55">{{ filteredVoices.length }} {{ t('voiceSelector.results', 'results') }}</span>
            <button type="button" class="btn btn-ghost btn-xs rounded-lg font-medium normal-case text-base-content/80 hover:bg-base-200/80" @click="openFullScreen">
              <Icon icon="mdi:arrow-expand-all" class="h-4 w-4" />
              <span :class="isRtl === true ? 'mr-1' : 'ml-1'">{{ t('voiceSelector.viewAll', 'View all') }}</span>
            </button>
          </div>
        </div>
        <div class="min-h-0 flex-1 overflow-y-auto p-2">
          <button
            v-for="voice in filteredVoices"
            :key="voice.voiceId"
            type="button"
            class="mb-1 w-full rounded-xl border px-3 py-2 text-left transition-all duration-150 last:mb-0"
            :class="voice.voiceId === pendingVoiceId ? 'border-base-content/25 bg-base-200/90 shadow-sm ring-1 ring-base-content/10 dark:bg-gray-800/90' : 'border-transparent hover:border-base-300/90 hover:bg-base-200/50 dark:hover:border-gray-600 dark:hover:bg-gray-800/60'"
            @click="setPendingVoice(voice.voiceId)"
          >
            <div class="flex w-full items-start">
              <div class="relative min-w-0 flex-1 overflow-hidden" :class="isRtl === true ? 'text-right' : 'text-left'">
                <div class="flex w-full items-center justify-between">
                  <h3 class="pr-2 text-sm font-semibold leading-tight text-base-content">{{ getVoiceDisplayName(voice) }}</h3>
                  <div class="flex flex-wrap justify-end">
                    <span v-if="formatVoiceLanguagesChipLabel(voice) !== ''" class="mr-1 rounded-md border border-base-300/60 bg-base-100/80 px-1.5 py-0.5 text-[10px] font-medium text-base-content/80 dark:border-gray-600 dark:bg-gray-900/50">{{ formatVoiceLanguagesChipLabel(voice) }}</span>
                    <span class="rounded-md border border-base-300/60 bg-base-100/80 px-1.5 py-0.5 text-[10px] font-medium text-base-content/80 dark:border-gray-600 dark:bg-gray-900/50">{{ voice.accent !== '' ? formatLanguageLabel(voice.accent, '') : t('voiceSelector.noAccent', 'No accent') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </button>
          <div v-if="filteredVoices.length === 0" class="rounded-xl border border-dashed border-base-300/80 px-4 py-8 text-center text-sm text-base-content/60 dark:border-gray-700">
            {{ t('voiceSelector.noResults', 'No voices matched your search.') }}
          </div>
        </div>
        <div class="shrink-0 border-t border-base-300/80 p-3 dark:border-gray-700/80">
          <div class="flex items-center justify-end">
            <button type="button" class="btn btn-ghost rounded-xl font-medium normal-case text-base-content/80 hover:bg-base-200/80" @click="closeDropdown">{{ t('voiceSelector.close', 'Close') }}</button>
            <button
              type="button"
              class="inline-flex items-center justify-center rounded-xl border border-base-content/15 bg-base-content px-4 py-2.5 text-sm font-semibold text-base-100 shadow-sm transition-all duration-150 hover:bg-base-content/90 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50"
              :class="isRtl === true ? 'mr-2' : 'ml-2'"
              :disabled="hasPendingSelectionChange !== true"
              @click="confirmPendingVoiceAndClose"
            >
              {{ t('voiceSelector.confirm', 'Confirm') }}
            </button>
          </div>
        </div>
        </div>
      </transition>
    </Teleport>
  </div>


  <Teleport to="body">
    <div v-if="isFullScreenOpen === true" class="fixed inset-0 z-[280] flex items-center justify-center bg-black/45 p-4 backdrop-blur-sm sm:p-6" @click="closeFullScreen">
      <div class="flex h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-3xl border border-base-300/60 bg-base-100 shadow-2xl ring-1 ring-black/5 dark:border-gray-700 dark:bg-gray-950 dark:ring-white/10" @click.stop>
        <div class="flex h-full flex-col overflow-hidden">
          <div class="border-b border-base-300/80 px-5 py-4 dark:border-gray-800">
            <div class="flex items-start justify-between">
              <div class="min-w-0 pr-3">
                <h3 class="text-lg font-bold tracking-tight text-base-content">{{ t('voiceSelector.title', 'Voice library') }}</h3>
                <p class="mt-1 text-sm text-base-content/65">{{ t('voiceSelector.subtitle', 'Search, compare and review voice details before selecting one.') }}</p>
              </div>
              <button type="button" class="btn btn-ghost btn-sm btn-circle shrink-0 rounded-xl" @click="closeFullScreen">
                <Icon icon="mdi:close" class="h-5 w-5" />
              </button>
            </div>
          </div>
          <div class="flex-1 overflow-hidden">
            <div class="flex h-full flex-col lg:flex-row">
              <div class="flex h-full flex-col border-b border-base-300/80 dark:border-gray-800 lg:w-[58%] lg:border-b-0 lg:border-r lg:border-base-300/80 dark:lg:border-gray-800">
                <div class="border-b border-base-300/80 p-4 dark:border-gray-800">
                  <div class="relative">
                    <input
                      ref="modalSearchInputRef"
                      v-model="searchQuery"
                      type="text"
                      class="input input-bordered h-11 w-full rounded-xl border-base-300/80 bg-base-100 pr-10 transition-shadow focus:border-base-content/20 focus:outline-none focus:ring-2 focus:ring-base-content/10 dark:border-gray-700 dark:bg-gray-950"
                      :class="isRtl === true ? 'pl-10 pr-4 text-right' : 'pr-10'"
                      :placeholder="t('voiceSelector.searchPlaceholder', 'Search voice, accent or language')"
                    />
                    <Icon
                      icon="mdi:magnify"
                      class="pointer-events-none absolute top-1/2 h-5 w-5 -translate-y-1/2 text-base-content/45"
                      :class="isRtl === true ? 'left-3' : 'right-3'"
                    />
                  </div>
                  <div class="mt-3 flex flex-row flex-nowrap space-x-2 overflow-x-auto pb-1 rtl:space-x-reverse hide-scrollbar">
                    <button
                      type="button"
                      class="cursor-pointer whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-all duration-150"
                      :class="selectedLanguageFilter === '' ? 'border-base-content/20 bg-base-content/10 text-base-content shadow-sm' : 'border-transparent bg-base-200/70 text-base-content/70 hover:bg-base-200 hover:text-base-content dark:bg-gray-800/80 dark:hover:bg-gray-800'"
                      @click="selectedLanguageFilter = ''"
                    >
                      {{ t('voiceSelector.all', 'All') }}
                    </button>
                    <button
                      v-for="lang in availableLanguages"
                      :key="lang"
                      type="button"
                      class="cursor-pointer whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-all duration-150"
                      :class="selectedLanguageFilter === lang ? 'border-base-content/20 bg-base-content/10 text-base-content shadow-sm' : 'border-transparent bg-base-200/70 text-base-content/70 hover:bg-base-200 hover:text-base-content dark:bg-gray-800/80 dark:hover:bg-gray-800'"
                      @click="selectedLanguageFilter = lang"
                    >
                      {{ formatLanguageLabel(lang) }}
                    </button>
                  </div>
                  <div class="mt-3 text-xs text-base-content/55">
                    {{ filteredVoices.length }} {{ t('voiceSelector.voicesAvailable', 'voices available') }}
                  </div>
                </div>
                <div class="flex-1 overflow-y-auto p-4">
                  <button
                    v-for="voice in filteredVoices"
                    :key="`modal-${voice.voiceId}`"
                    type="button"
                    class="mb-3 w-full rounded-2xl border p-4 text-left transition-all duration-150 last:mb-0"
                    :class="voice.voiceId === pendingVoiceId ? 'border-base-content/30 bg-base-200/95 shadow-md ring-1 ring-base-content/10 dark:bg-gray-800/95' : detailVoiceId === voice.voiceId ? 'border-base-300/90 bg-base-200/40 dark:border-gray-700 dark:bg-gray-900/80' : 'border-base-300/70 hover:border-base-content/20 hover:bg-base-200/40 dark:border-gray-800 dark:hover:border-gray-600 dark:hover:bg-gray-900/70'"
                    @mouseenter="previewVoiceDetails(voice.voiceId)"
                    @focus="previewVoiceDetails(voice.voiceId)"
                    @click="setPendingVoice(voice.voiceId)"
                  >
                    <div class="flex w-full items-start">
                      <div class="min-w-0 flex-1" :class="isRtl === true ? 'text-right' : 'text-left'">
                        <div class="flex items-start justify-between">
                          <div class="min-w-0 pr-3">
                            <h4 class="text-sm font-semibold tracking-tight text-base-content">{{ getVoiceDisplayName(voice) }}</h4>
                          </div>
                        </div>
                        <div class="mt-3 flex flex-wrap">
                          <span v-if="formatVoiceLanguagesChipLabel(voice) !== ''" class="mb-2 mr-2 rounded-md border border-base-300/70 bg-base-100/90 px-2 py-0.5 text-[11px] font-medium text-base-content/80 dark:border-gray-600 dark:bg-gray-950/60">{{ formatVoiceLanguagesChipLabel(voice) }}</span>
                          <span class="mb-2 rounded-md border border-base-300/70 bg-base-100/90 px-2 py-0.5 text-[11px] font-medium text-base-content/80 dark:border-gray-600 dark:bg-gray-950/60">{{ voice.accent !== '' ? formatLanguageLabel(voice.accent, '') : t('voiceSelector.noAccent', 'No accent') }}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                  <div v-if="filteredVoices.length === 0" class="rounded-2xl border border-dashed border-base-300/80 px-4 py-12 text-center text-sm text-base-content/60 dark:border-gray-700">
                    {{ t('voiceSelector.noResults', 'No voices matched your search.') }}
                  </div>
                </div>
              </div>
              <div class="flex h-full flex-col lg:w-[42%]">
                <div class="border-b border-base-300/80 px-5 py-4 dark:border-gray-800">
                  <h4 class="text-sm font-semibold tracking-tight text-base-content">{{ t('voiceSelector.details', 'Voice details') }}</h4>
                </div>
                <div class="flex-1 overflow-y-auto px-5 py-4">
                  <div v-if="activeVoiceDetails !== null">
                    <div class="rounded-2xl border border-base-300/80 p-4 dark:border-gray-800">
                      <div class="flex items-start">
                        <div class="min-w-0" :class="isRtl === true ? 'text-right' : 'text-left'">
                          <h4 class="text-lg font-bold tracking-tight text-base-content">{{ activeVoiceDisplayName }}</h4>
                          <div class="mt-4 flex flex-wrap">
                            <span v-if="formatVoiceLanguagesChipLabel(activeVoiceDetails) !== ''" class="mb-2 mr-2 rounded-md border border-base-content/15 bg-base-content/10 px-2 py-0.5 text-xs font-semibold text-base-content">{{ formatVoiceLanguagesChipLabel(activeVoiceDetails) }}</span>
                            <span v-if="activeVoiceAccent !== ''" class="mb-2 mr-2 rounded-md border border-base-300/70 bg-base-100/80 px-2 py-0.5 text-xs font-medium text-base-content/80 dark:border-gray-600 dark:bg-gray-950/60">{{ formatLanguageLabel(activeVoiceAccent, '') }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="mt-4 rounded-2xl border border-base-300/80 p-4 dark:border-gray-800">
                      <div class="text-xs font-semibold uppercase tracking-wide text-base-content/50">{{ t('voiceSelector.info', 'Information') }}</div>
                      <div class="mt-4">
                        <div v-if="showAccentSelector === true" class="mb-3">
                          <div class="text-xs font-semibold uppercase tracking-wide text-base-content/50">{{ t('voiceSelector.accent', 'Accent') }}</div>
                          <p class="mt-1 text-xs text-base-content/65">{{ t('voiceSelector.accentLanguageHint', 'Chooses the language used for this voice.') }}</p>
                          <div class="mt-3 rounded-2xl border border-base-300/80 bg-base-100/60 p-3 dark:border-gray-800 dark:bg-gray-950/40">
                            <div class="relative">
                              <input
                                v-model="accentSearchQuery"
                                type="text"
                                class="input input-bordered h-10 w-full rounded-xl border-base-300/80 bg-base-100 pr-10 text-sm transition-shadow focus:border-base-content/20 focus:outline-none focus:ring-2 focus:ring-base-content/10 dark:border-gray-700 dark:bg-gray-950"
                                :class="isRtl === true ? 'pl-10 pr-4 text-right' : 'pr-10'"
                                :placeholder="t('voiceSelector.searchAccentPlaceholder', 'Search accent or language')"
                              />
                              <Icon
                                icon="mdi:magnify"
                                class="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-base-content/45"
                                :class="isRtl === true ? 'left-3' : 'right-3'"
                              />
                            </div>
                            <div class="mt-3 max-h-64 overflow-y-auto p-1">
                              <button
                                v-for="accentOption in filteredPendingAccentOptions"
                                :key="accentOption"
                                type="button"
                                class="mb-3 w-full rounded-2xl border p-4 text-left transition-all duration-150 last:mb-0"
                                :class="pendingAccentModel === accentOption ? 'border-base-content/30 bg-base-200/95 shadow-md ring-1 ring-base-content/10 dark:bg-gray-800/95' : 'border-base-300/70 hover:border-base-content/20 hover:bg-base-200/40 dark:border-gray-800 dark:hover:border-gray-600 dark:hover:bg-gray-900/70'"
                                @click="pendingAccentModel = accentOption"
                              >
                                <div class="flex w-full items-center justify-between">
                                  <div class="flex min-w-0 flex-1 items-center">
                                    <div class="min-w-0 flex-1 overflow-hidden" :class="isRtl === true ? 'text-right' : 'text-left'">
                                      <h4 class="truncate text-sm font-semibold tracking-tight text-base-content">{{ formatLanguageLabel(accentOption, '') }}</h4>
                                    </div>
                                  </div>
                                  <Icon
                                    v-if="pendingAccentModel === accentOption"
                                    icon="heroicons:check-20-solid"
                                    class="h-5 w-5 flex-shrink-0 text-base-content"
                                    style="margin-inline-start: 10px;"
                                  />
                                </div>
                              </button>
                              <div
                                v-if="filteredPendingAccentOptions.length === 0"
                                class="rounded-2xl border border-dashed border-base-300/80 px-4 py-10 text-center text-sm text-base-content/60 dark:border-gray-700"
                              >
                                {{ t('voiceSelector.noAccentResults', 'No accents matched your search.') }}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div v-else class="mb-3">
                          <div class="text-xs opacity-60">{{ t('voiceSelector.accent', 'Accent') }}</div>
                          <div class="text-sm font-medium mt-1">{{ activeVoiceAccent !== '' ? formatLanguageLabel(activeVoiceAccent, activeVoiceAccent) : t('voiceSelector.noAccent', 'No accent') }}</div>
                        </div>
                        <div class="mb-3">
                          <div class="text-xs opacity-60">{{ t('voiceSelector.language', 'Language') }}</div>
                          <div v-if="getVoiceLanguagesList(activeVoiceDetails).length <= 1" class="text-sm font-medium mt-1">
                            {{ getVoiceLanguagesList(activeVoiceDetails).length === 1 ? formatLanguageLabel(getVoiceLanguagesList(activeVoiceDetails)[0], activeVoiceAccent) : formatLanguageLabel(activeVoiceDetails.language, activeVoiceAccent) }}
                          </div>
                          <div
                            v-else
                            class="mt-2 max-h-44 overflow-y-auto rounded-xl border border-base-300/70 bg-base-100/50 p-2 dark:border-gray-700 dark:bg-gray-900/40"
                          >
                            <span
                              v-for="lang in getVoiceLanguagesList(activeVoiceDetails)"
                              :key="lang"
                              class="mb-2 mr-2 inline-flex rounded-md border border-base-300/70 bg-base-100/90 px-2 py-0.5 text-[11px] font-medium text-base-content/80 dark:border-gray-600 dark:bg-gray-950/60"
                            >{{ formatLanguageLabel(lang, '') }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="rounded-xl border border-dashed border-base-300 px-4 py-10 text-center text-sm text-base-content opacity-70 dark:border-gray-700">
                    {{ t('voiceSelector.noVoiceSelected', 'Select a voice to review its details.') }}
                  </div>
                </div>
                <div class="border-t border-base-300/80 px-5 py-4 dark:border-gray-800">
                  <div class="flex items-center justify-end">
                    <button type="button" class="btn btn-ghost rounded-xl font-medium normal-case" @click="closeFullScreen">{{ t('voiceSelector.close', 'Close') }}</button>
                    <button
                      v-if="activeVoiceDetails !== null && hasPendingSelectionChange === true"
                      type="button"
                      class="inline-flex items-center justify-center rounded-xl border border-base-content/15 bg-base-content px-4 py-2.5 text-sm font-semibold text-base-100 shadow-sm transition-all duration-150 hover:bg-base-content/90 active:scale-[0.98]"
                      :class="isRtl === true ? 'mr-2' : 'ml-2'"
                      @click="confirmPendingVoiceAndCloseFullScreen"
                    >
                      {{ t('voiceSelector.confirm', 'Confirm') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';
import { useI18n } from '@/i18n';
import { defaultVoiceId, voiceOptions } from '../utils/voiceCatalog.js';
import { collectFeminineVoiceSearchTerms, isFeminineVoice, resolveVoiceDisplayName } from '../utils/feminineVoiceDisplay.js';
import { getLocalizedLanguageName } from '../utils/geminiLanguages.js';

const props = defineProps({
  modelValue: { type: String, default: defaultVoiceId },
  placeholder: { type: String, default: 'Select a Voice' },
  accent: { type: String, default: '' },
  accentEnabled: { type: Boolean, default: false },
  provider: { type: String, default: '' },
  voiceType: { type: String, default: '' },
  voiceFilterMode: { type: String, default: '' },
  agentLanguage: { type: String, default: '' }
});
const emit = defineEmits(['update:modelValue', 'update:accent']);

const { t, currentLanguage } = useI18n();
const isRtl = computed(() => (['he', 'ar'].includes(currentLanguage.value) === true) ? true : false);

const isOpen = ref(false);
const isFullScreenOpen = ref(false);
const rootRef = ref(null);
const dropdownListRef = ref(null);
const searchInputRef = ref(null);
const modalSearchInputRef = ref(null);
const searchQuery = ref('');
const selectedLanguageFilter = ref('');
const detailVoiceId = ref('');
const pendingVoiceId = ref('');
const pendingAccentValue = ref('');

const dropdownZIndex = 265;
const geminiLiveVoiceIds = ['Achird', 'Charon', 'Schedar', 'Kore', 'Aoede', 'Callirrhoe'];
const dropdownLayout = ref({
  top: '0px',
  left: '0px',
  width: '320px',
  maxHeight: '320px'
});
const dropdownPanelStyle = computed(() => ({
  position: 'fixed',
  zIndex: dropdownZIndex,
  top: dropdownLayout.value.top,
  left: dropdownLayout.value.left,
  width: dropdownLayout.value.width,
  height: dropdownLayout.value.maxHeight,
  maxHeight: dropdownLayout.value.maxHeight
}));

function updateDropdownPosition() {
  if (rootRef.value === null) {
    return;
  }
  if (isOpen.value !== true) {
    return;
  }
  const rect = rootRef.value.getBoundingClientRect();
  const margin = 8;
  const vh = typeof window.innerHeight === 'number' ? window.innerHeight : 800;
  const vw = typeof window.innerWidth === 'number' ? window.innerWidth : 400;
  const spaceBelow = vh - rect.bottom - margin;
  const spaceAbove = rect.top - margin;
  let topPx;
  let maxHPx;
  if (spaceBelow >= 360 || spaceBelow >= spaceAbove) {
    topPx = rect.bottom + margin;
    maxHPx = Math.min(540, Math.max(120, spaceBelow - 4));
  } else {
    maxHPx = Math.min(540, Math.max(120, spaceAbove - 4));
    topPx = rect.top - maxHPx - margin;
    if (topPx < margin) {
      topPx = margin;
      maxHPx = Math.max(120, rect.top - margin * 2);
    }
  }
  let leftPx = rect.left;
  let widthPx = rect.width;
  if (leftPx < margin) {
    leftPx = margin;
  }
  if (leftPx + widthPx > vw - margin) {
    leftPx = Math.max(margin, vw - widthPx - margin);
  }
  if (widthPx > vw - margin * 2) {
    widthPx = Math.max(200, vw - margin * 2);
  }
  dropdownLayout.value = {
    top: `${Math.round(topPx)}px`,
    left: `${Math.round(leftPx)}px`,
    width: `${Math.round(widthPx)}px`,
    maxHeight: `${Math.round(maxHPx)}px`
  };
}

function bindDropdownPositionListeners() {
  window.addEventListener('resize', updateDropdownPosition);
  document.addEventListener('scroll', updateDropdownPosition, true);
}

function unbindDropdownPositionListeners() {
  window.removeEventListener('resize', updateDropdownPosition);
  document.removeEventListener('scroll', updateDropdownPosition, true);
}

function isVoiceVisible(voiceParam) {
  const voice = voiceParam !== null && voiceParam !== undefined && typeof voiceParam === 'object' ? voiceParam : null;
  if (voice === null) {
    return false;
  }
  return voice.hidden !== true;
}

function normalizeVoiceType(valueParam) {
  const normalized = typeof valueParam === 'string' ? valueParam.trim().toLowerCase().replace(/[^a-z0-9]+/g, '') : '';
  if (normalized === 'vtv') {
    return 'vtv';
  }
  if (normalized === 'default') {
    return 'vtv';
  }
  if (['geminilive', 'geminirealtime', 'lite'].includes(normalized) === true) {
    return 'geminiLive';
  }
  return '';
}

function normalizeVoiceFilterMode(valueParam) {
  const normalized = typeof valueParam === 'string' ? valueParam.trim().toLowerCase().replace(/[^a-z0-9]+/g, '') : '';
  if (normalized === 'default') {
    return 'default';
  }
  if (normalized === 'quality') {
    return 'quality';
  }
  if (normalized === 'v3flash') {
    return 'v3flash';
  }
  if (normalized === 'v3duplex' || normalized === 'duplex') {
    return 'v3duplex';
  }
  return '';
}

function getVoiceProvider(voiceParam) {
  const voice = voiceParam !== null && voiceParam !== undefined && typeof voiceParam === 'object' ? voiceParam : {};
  const providerRaw = typeof voice.providerId === 'string' ? voice.providerId : '';
  return typeof providerRaw === 'string' ? providerRaw.trim().toLowerCase() : '';
}

function getVoiceRuntimeType(voiceParam) {
  const voice = voiceParam !== null && voiceParam !== undefined && typeof voiceParam === 'object' ? voiceParam : {};
  return typeof voice.runtimeType === 'string' ? voice.runtimeType.trim().toLowerCase() : '';
}

function getVoiceEngineVersion(voiceParam) {
  const voice = voiceParam !== null && voiceParam !== undefined && typeof voiceParam === 'object' ? voiceParam : {};
  const engineVersion = typeof voice.engineVersion === 'string' ? voice.engineVersion.trim().toLowerCase() : '';
  if (engineVersion !== '') {
    return engineVersion;
  }
  const provider = getVoiceProvider(voice);
  if (provider === 'inworld' || provider === 'inworldai') {
    return 'v3flash';
  }
  return '';
}

function isDefaultRealtimeVoice(voiceParam) {
  if (getVoiceRuntimeType(voiceParam) === 'default') {
    return true;
  }
  return false;
}

function isGeminiLiveVoice(voiceParam) {
  const voice = voiceParam !== null && voiceParam !== undefined && typeof voiceParam === 'object' ? voiceParam : {};
  const provider = getVoiceProvider(voice);
  if (provider === 'googleai' || provider === 'aigooglestudio' || provider === 'googleaistudio') {
    return true;
  }
  const voiceId = typeof voice.voiceId === 'string' ? voice.voiceId.trim().toLowerCase() : '';
  for (let index = 0; index < geminiLiveVoiceIds.length; index += 1) {
    if (geminiLiveVoiceIds[index].toLowerCase() === voiceId) {
      return true;
    }
  }
  return false;
}

function isGptLiveVoice(voiceParam) {
  const voice = voiceParam !== null && voiceParam !== undefined && typeof voiceParam === 'object' ? voiceParam : {};
  const voiceId = typeof voice.voiceId === 'string' ? voice.voiceId.trim().toLowerCase() : '';
  const supportedVoiceIds = ['alloy', 'ash', 'ballad', 'coral', 'echo', 'sage', 'shimmer', 'verse', 'marin', 'cedar', 'quartz', 'ripple', 'vesper', 'willow', 'stone', 'gleam', 'meridian', 'bossa', 'tempo', 'beacon', 'delta', 'cinder'];
  return getVoiceEngineVersion(voice) === 'v3duplex' && supportedVoiceIds.includes(voiceId) === true;
}

function supportsVoiceType(voiceParam, voiceTypeParam) {
  const filterMode = normalizeVoiceFilterMode(props.voiceFilterMode);
  if (filterMode === 'default') {
    return isDefaultRealtimeVoice(voiceParam);
  }
  if (filterMode === 'quality') {
    return isDefaultRealtimeVoice(voiceParam) !== true;
  }
  if (filterMode === 'v3flash') {
    return getVoiceEngineVersion(voiceParam) === 'v3flash';
  }
  if (filterMode === 'v3duplex') {
    return isGptLiveVoice(voiceParam);
  }
  const voiceType = normalizeVoiceType(voiceTypeParam);
  if (voiceType === '') {
    return true;
  }
  if (voiceType === 'vtv' && isDefaultRealtimeVoice(voiceParam) === true) {
    return true;
  }
  if (voiceType === 'geminiLive' && isGeminiLiveVoice(voiceParam) === true) {
    return true;
  }
  return false;
}

const voices = computed(() => {
  const visibleVoices = [];
  const provider = typeof props.provider === 'string' ? props.provider.trim().toLowerCase() : '';
  for (let index = 0; index < voiceOptions.length; index += 1) {
    const voice = voiceOptions[index];
    const voiceProvider = getVoiceProvider(voice);
    const gptLiveVoice = normalizeVoiceFilterMode(props.voiceFilterMode) === 'v3duplex' && isGptLiveVoice(voice) === true;
    if ((isVoiceVisible(voice) === true || gptLiveVoice === true) && supportsVoiceType(voice, props.voiceType) === true && (provider === '' || voiceProvider === provider)) {
      visibleVoices.push(voice);
    }
  }
  return visibleVoices;
});

function getVoiceLanguagesList(voiceParam) {
  const voice = voiceParam !== null && voiceParam !== undefined && typeof voiceParam === 'object' ? voiceParam : null;
  if (voice === null) {
    return [];
  }
  const raw = Array.isArray(voice.languages) === true ? voice.languages : [];
  const out = [];
  const seen = new Set();
  for (let index = 0; index < raw.length; index += 1) {
    const item = raw[index];
    if (typeof item === 'string' && item.trim() !== '') {
      const trimmed = item.trim();
      const key = trimmed.toLowerCase();
      if (seen.has(key) !== true) {
        seen.add(key);
        out.push(trimmed);
      }
    }
  }
  if (out.length > 0) {
    return out;
  }
  const primary = typeof voice.language === 'string' ? voice.language.trim() : '';
  if (primary !== '') {
    return [primary];
  }
  return [];
}

function voiceMatchesLanguageFilter(voiceParam, filterLang) {
  const filter = typeof filterLang === 'string' ? filterLang.trim() : '';
  if (filter === '') {
    return true;
  }
  const list = getVoiceLanguagesList(voiceParam);
  for (let index = 0; index < list.length; index += 1) {
    if (list[index] === filter) {
      return true;
    }
  }
  return false;
}

function formatVoiceLanguagesChipLabel(voiceParam) {
  const list = getVoiceLanguagesList(voiceParam);
  if (list.length === 0) {
    return '';
  }
  const visibleLanguageNames = [];
  const maximumVisibleLanguageNames = 2;
  for (let index = 0; index < list.length && index < maximumVisibleLanguageNames; index += 1) {
    visibleLanguageNames.push(formatLanguageLabel(list[index], ''));
  }
  const remainingLanguageCount = list.length - visibleLanguageNames.length;
  if (remainingLanguageCount > 0) {
    return `${visibleLanguageNames.join(', ')} +${remainingLanguageCount}`;
  }
  return visibleLanguageNames.join(', ');
}

const availableLanguages = computed(() => {
  const langs = new Set();
  voices.value.forEach((v) => {
    const list = getVoiceLanguagesList(v);
    if (list.length > 0) {
      for (let index = 0; index < list.length; index += 1) {
        langs.add(list[index]);
      }
    } else if (typeof v.language === 'string' && v.language.trim() !== '') {
      langs.add(v.language.trim());
    }
  });
  return Array.from(langs).sort((a, b) => a.localeCompare(b));
});

const filteredVoices = computed(() => {
  let list = voices.value;
  if (selectedLanguageFilter.value !== '') {
    list = list.filter((v) => voiceMatchesLanguageFilter(v, selectedLanguageFilter.value) === true);
  }

  const query = typeof searchQuery.value === 'string' ? searchQuery.value.trim().toLowerCase() : '';
  if (query === '') {
    return list;
  }
  return list.filter((voice) => {
    const langList = getVoiceLanguagesList(voice);
    const aliasTerms = collectFeminineVoiceSearchTerms(voice);
    const haystack = [
      voice.displayName,
      voice.voiceName,
      voice.description,
      voice.language,
      voice.accent,
      voice.tierLabel,
      getVoiceDisplayName(voice),
      ...langList,
      ...aliasTerms
    ].join(' ').toLowerCase();
    return haystack.includes(query) === true;
  });
});

const selectedVoiceObj = computed(() => {
  const found = voices.value.find((voice) => voice.voiceId === props.modelValue);
  if (found !== undefined && found !== null) {
    return found;
  }
  if (voices.value.length > 0) {
    return voices.value[0];
  }
  return null;
});

function findVoiceById(voiceIdParam) {
  const voiceId = typeof voiceIdParam === 'string' ? voiceIdParam.trim() : '';
  if (voiceId === '') {
    return null;
  }
  const found = voices.value.find((voice) => voice.voiceId === voiceId);
  if (found !== undefined && found !== null) {
    return found;
  }
  return null;
}

function getAccentOptionsForVoice(voiceParam) {
  const voice = voiceParam !== null && voiceParam !== undefined && typeof voiceParam === 'object' ? voiceParam : null;
  const accentOptionsRaw = Array.isArray(voice?.accentOptions) === true ? voice.accentOptions : [];
  const languagesRaw = Array.isArray(voice?.languages) === true ? voice.languages : [];
  const raw = accentOptionsRaw.length > 0 ? accentOptionsRaw : languagesRaw;
  const accentOptions = [];
  for (let index = 0; index < raw.length; index += 1) {
    const item = raw[index];
    if (typeof item === 'string' && item.trim() !== '') {
      accentOptions.push(item.trim());
    }
  }
  return [...new Set(accentOptions)];
}

function resolveAccentOption(accentOptionsParam, valueParam) {
  const accentOptions = Array.isArray(accentOptionsParam) === true ? accentOptionsParam : [];
  const value = typeof valueParam === 'string' ? valueParam.trim().toLowerCase() : '';
  if (value === '') {
    return '';
  }
  for (let index = 0; index < accentOptions.length; index += 1) {
    const item = accentOptions[index];
    if (typeof item === 'string' && item.trim().toLowerCase() === value) {
      return item.trim();
    }
  }
  return '';
}

function resolveVoiceAccentLabel(voiceParam, accentParam) {
  const voice = voiceParam !== null && voiceParam !== undefined && typeof voiceParam === 'object' ? voiceParam : null;
  const accentOptions = getAccentOptionsForVoice(voice);
  const resolvedAccent = resolveAccentOption(accentOptions, accentParam);
  if (resolvedAccent !== '') {
    return resolvedAccent;
  }
  return typeof voice?.accent === 'string' ? voice.accent.trim() : '';
}

function resolvePreferredAccentValue(voiceParam, preferredAccentParam, fallbackAccentParam) {
  const voice = voiceParam !== null && voiceParam !== undefined && typeof voiceParam === 'object' ? voiceParam : null;
  const accentOptions = getAccentOptionsForVoice(voice);
  if (props.accentEnabled !== true || accentOptions.length <= 0) {
    return '';
  }
  const preferredAccent = resolveAccentOption(accentOptions, preferredAccentParam);
  if (preferredAccent !== '') {
    return preferredAccent;
  }
  const fallbackAccent = resolveAccentOption(accentOptions, fallbackAccentParam);
  if (fallbackAccent !== '') {
    return fallbackAccent;
  }
  const voiceLanguage = typeof voice?.language === 'string' ? voice.language.trim() : '';
  const matchingLanguage = resolveAccentOption(accentOptions, voiceLanguage);
  if (matchingLanguage !== '') {
    return matchingLanguage;
  }
  return accentOptions[0];
}

function syncPendingAccentWithVoice(voiceParam, accentParam, fallbackAccentParam) {
  pendingAccentValue.value = resolvePreferredAccentValue(voiceParam, accentParam, fallbackAccentParam);
}

const pendingVoiceObj = computed(() => {
  const pendingVoice = findVoiceById(pendingVoiceId.value);
  if (pendingVoice !== null) {
    return pendingVoice;
  }
  return selectedVoiceObj.value;
});

const pendingVoiceAccentOptions = computed(() => getAccentOptionsForVoice(pendingVoiceObj.value));
const accentSearchQuery = ref('');
const filteredPendingAccentOptions = computed(() => {
  const list = pendingVoiceAccentOptions.value;
  const query = typeof accentSearchQuery.value === 'string' ? accentSearchQuery.value.trim().toLowerCase() : '';
  if (query === '') {
    return list;
  }
  const filtered = [];
  for (let index = 0; index < list.length; index += 1) {
    const item = list[index];
    if (typeof item === 'string' && item.toLowerCase().includes(query) === true) {
      filtered.push(item);
    }
  }
  return filtered;
});
const selectedVoiceAccent = computed(() => resolveVoiceAccentLabel(selectedVoiceObj.value, props.accent));
const selectedVoiceDisplayName = computed(() => {
  if (selectedVoiceObj.value === null) {
    return '';
  }
  return getVoiceDisplayName(selectedVoiceObj.value, selectedVoiceAccent.value);
});
const isSelectedVoiceFeminine = computed(() => isFeminineVoice(selectedVoiceObj.value));
const activeVoiceDisplayName = computed(() => {
  if (activeVoiceDetails.value === null) {
    return '';
  }
  return getVoiceDisplayName(activeVoiceDetails.value, activeVoiceAccent.value);
});
const isActiveVoiceFeminine = computed(() => isFeminineVoice(activeVoiceDetails.value));

function getVoiceDisplayName(voiceParam, accentParam) {
  return resolveVoiceDisplayName(voiceParam, currentLanguage.value, accentParam);
}
const activeVoiceAccent = computed(() => {
  if (activeVoiceDetails.value === null) {
    return '';
  }
  const pendingVoice = pendingVoiceObj.value;
  const accentSource = activeVoiceDetails.value.voiceId === (pendingVoice !== null ? pendingVoice.voiceId : '') ? pendingAccentValue.value : props.accent;
  return resolveVoiceAccentLabel(activeVoiceDetails.value, accentSource);
});
const showAccentSelector = computed(() => {
  if (props.accentEnabled !== true) {
    return false;
  }
  if (activeVoiceDetails.value === null || pendingVoiceObj.value === null) {
    return false;
  }
  if (activeVoiceDetails.value.voiceId !== pendingVoiceObj.value.voiceId) {
    return false;
  }
  return pendingVoiceAccentOptions.value.length > 0;
});
const pendingAccentModel = computed({
  get: () => pendingAccentValue.value,
  set: (value) => {
    pendingAccentValue.value = resolvePreferredAccentValue(pendingVoiceObj.value, value, props.accent);
  }
});
const hasPendingAccentChange = computed(() => {
  if (props.accentEnabled !== true || pendingVoiceObj.value === null || pendingVoiceAccentOptions.value.length <= 0) {
    return false;
  }
  const currentAccent = resolvePreferredAccentValue(pendingVoiceObj.value, props.accent, '');
  const pendingAccent = resolvePreferredAccentValue(pendingVoiceObj.value, pendingAccentValue.value, props.accent);
  if (pendingAccent === '') {
    return false;
  }
  return pendingAccent !== currentAccent;
});
const hasPendingSelectionChange = computed(() => {
  const pendingId = typeof pendingVoiceId.value === 'string' ? pendingVoiceId.value.trim() : '';
  const modelId = typeof props.modelValue === 'string' ? props.modelValue.trim() : '';
  if (pendingId !== '' && pendingId !== modelId) {
    return true;
  }
  return hasPendingAccentChange.value === true;
});

const activeVoiceDetails = computed(() => {
  const activeId = typeof detailVoiceId.value === 'string' ? detailVoiceId.value.trim() : '';
  if (activeId !== '') {
    const found = voices.value.find((voice) => voice.voiceId === activeId);
    if (found !== undefined && found !== null) {
      return found;
    }
  }
  return selectedVoiceObj.value;
});

function buildMetaLine(voice) {
  if (voice === null || voice === undefined) {
    return '';
  }
  const list = getVoiceLanguagesList(voice);
  if (list.length > 0) {
    return t('voiceSelector.supportedLanguagesCount', 'Supports {n} languages', { n: list.length });
  }
  return '';
}

function formatLanguageLabel(language, accent) {
  const normalizedLanguage = typeof language === 'string' ? language.trim() : '';
  if (normalizedLanguage === '') {
    return '';
  }
  return getLocalizedLanguageName(normalizedLanguage, currentLanguage.value);
}

function focusSearchInput() {
  nextTick(() => {
    if (isFullScreenOpen.value === true) {
      if (modalSearchInputRef.value !== null) {
        modalSearchInputRef.value.focus();
      }
      return;
    }
    if (searchInputRef.value !== null) {
      searchInputRef.value.focus();
    }
  });
}

function openDropdown() {
  syncPendingVoiceWithModel();
  setDetailVoiceId(pendingVoiceId.value);
  isOpen.value = true;
  focusSearchInput();
}

function closeDropdown() {
  isOpen.value = false;
}

function toggleDropdown() {
  if (isOpen.value === true) {
    closeDropdown();
    return;
  }
  openDropdown();
}

function setDetailVoiceId(voiceId) {
  detailVoiceId.value = typeof voiceId === 'string' ? voiceId : '';
}

function setPendingVoiceId(voiceId) {
  pendingVoiceId.value = typeof voiceId === 'string' ? voiceId : '';
}

function syncPendingVoiceWithModel() {
  const currentId = typeof props.modelValue === 'string' ? props.modelValue.trim() : '';
  if (currentId !== '') {
    pendingVoiceId.value = currentId;
    syncPendingAccentWithVoice(findVoiceById(currentId), props.accent, props.accent);
    return;
  }
  if (voices.value.length > 0) {
    pendingVoiceId.value = voices.value[0].voiceId;
    syncPendingAccentWithVoice(voices.value[0], props.accent, props.accent);
    return;
  }
  pendingVoiceId.value = '';
  pendingAccentValue.value = '';
}

function previewVoiceDetails(voiceId) {
  const pendingId = typeof pendingVoiceId.value === 'string' ? pendingVoiceId.value.trim() : '';
  if (pendingId !== '' && pendingId !== props.modelValue) {
    return;
  }
  setDetailVoiceId(voiceId);
}

function setPendingVoice(voiceId) {
  setPendingVoiceId(voiceId);
  setDetailVoiceId(voiceId);
  syncPendingAccentWithVoice(findVoiceById(voiceId), pendingAccentValue.value, props.accent);
}

function confirmPendingVoice() {
  const nextId = typeof pendingVoiceId.value === 'string' ? pendingVoiceId.value.trim() : '';
  if (nextId === '') {
    return;
  }
  if (props.modelValue !== nextId) {
    emit('update:modelValue', nextId);
  }
  const pendingVoice = pendingVoiceObj.value;
  if (props.accentEnabled === true && pendingVoice !== null && pendingVoiceAccentOptions.value.length > 0) {
    const nextAccent = resolvePreferredAccentValue(pendingVoice, pendingAccentValue.value, props.accent);
    pendingAccentValue.value = nextAccent;
    if (nextAccent !== '' && nextAccent !== props.accent) {
      emit('update:accent', nextAccent);
    }
  }
  setPendingVoiceId(nextId);
  setDetailVoiceId(nextId);
}

function confirmPendingVoiceAndClose() {
  confirmPendingVoice();
  closeDropdown();
}

function confirmPendingVoiceAndCloseFullScreen() {
  confirmPendingVoice();
  closeFullScreen();
}

function openFullScreen() {
  closeDropdown();
  syncPendingVoiceWithModel();
  isFullScreenOpen.value = true;
  if (pendingVoiceId.value !== '') {
    detailVoiceId.value = pendingVoiceId.value;
  } else if (selectedVoiceObj.value !== null) {
    detailVoiceId.value = selectedVoiceObj.value.voiceId;
  }
  focusSearchInput();
}

function closeFullScreen() {
  isFullScreenOpen.value = false;
}

function ensureValidSelectedVoice() {
  if (Array.isArray(voices.value) !== true || voices.value.length === 0) {
    pendingAccentValue.value = '';
    return;
  }
  const currentId = typeof props.modelValue === 'string' ? props.modelValue.trim() : '';
  const found = voices.value.find((voice) => voice.voiceId === currentId);
  if (found === undefined || found === null) {
    emit('update:modelValue', voices.value[0].voiceId);
    detailVoiceId.value = voices.value[0].voiceId;
    pendingVoiceId.value = voices.value[0].voiceId;
    syncPendingAccentWithVoice(voices.value[0], props.accent, props.accent);
    return;
  }
  if (detailVoiceId.value === '') {
    detailVoiceId.value = found.voiceId;
  }
  if (pendingVoiceId.value === '') {
    pendingVoiceId.value = found.voiceId;
    syncPendingAccentWithVoice(found, props.accent, props.accent);
    return;
  }
  const pendingFound = voices.value.find((voice) => voice.voiceId === pendingVoiceId.value);
  if (pendingFound === undefined || pendingFound === null) {
    pendingVoiceId.value = found.voiceId;
  }
  syncPendingAccentWithVoice(findVoiceById(pendingVoiceId.value), pendingAccentValue.value, props.accent);
}

function syncDetailVoiceWithFilter() {
  if (filteredVoices.value.length === 0) {
    return;
  }
  const found = filteredVoices.value.find((voice) => voice.voiceId === detailVoiceId.value);
  if (found === undefined || found === null) {
    detailVoiceId.value = filteredVoices.value[0].voiceId;
  }
}

function handleClickOutside(event) {
  if (isOpen.value !== true) {
    return;
  }
  if (rootRef.value !== null && rootRef.value.contains(event.target) === true) {
    return;
  }
  if (dropdownListRef.value !== null && dropdownListRef.value.contains(event.target) === true) {
    return;
  }
  closeDropdown();
}

function handleWindowKeydown(event) {
  if (event.key === 'Escape') {
    if (isFullScreenOpen.value === true) {
      closeFullScreen();
      return;
    }
    if (isOpen.value === true) {
      closeDropdown();
    }
  }
}

watch(voices, () => {
  ensureValidSelectedVoice();
}, { immediate: true });

watch(() => pendingVoiceId.value, () => {
  accentSearchQuery.value = '';
});

watch(() => props.modelValue, (nextValue) => {
  ensureValidSelectedVoice();
  setDetailVoiceId(nextValue);
  syncPendingVoiceWithModel();
});

watch(() => props.accent, (nextValue) => {
  if (isOpen.value === true || isFullScreenOpen.value === true) {
    return;
  }
  syncPendingAccentWithVoice(selectedVoiceObj.value, nextValue, nextValue);
});

watch(searchQuery, () => {
  syncDetailVoiceWithFilter();
});

watch(selectedLanguageFilter, () => {
  syncDetailVoiceWithFilter();
});

watch(isOpen, (nextValue) => {
  if (nextValue === true) {
    nextTick(() => {
      updateDropdownPosition();
      bindDropdownPositionListeners();
      focusSearchInput();
    });
    return;
  }
  unbindDropdownPositionListeners();
});

watch(isFullScreenOpen, (nextValue) => {
  if (nextValue === true) {
    focusSearchInput();
  }
});

onMounted(() => {
  document.addEventListener('click', handleClickOutside, true);
  window.addEventListener('keydown', handleWindowKeydown);
});

onBeforeUnmount(() => {
  unbindDropdownPositionListeners();
  document.removeEventListener('click', handleClickOutside, true);
  window.removeEventListener('keydown', handleWindowKeydown);
});
</script>
